import {Component, OnInit} from '@angular/core';
import {AngularCsv} from 'angular-csv-ext/dist/Angular-csv';
import {AssociationMongo} from 'app/models/association';
import {ApplicationType} from 'app/models/application';
import {ApplicationsService} from 'app/services/applications.service';
import {AssociationsService} from 'app/services/associations.service';
import {Alert} from 'app/shared/alerts/simple-message/simple-message.component';
import {SessionStorage} from 'app/storage/session.storage';
import {SopStorage} from 'app/storage/sop.storage';
import {catchError, EMPTY, forkJoin, of} from 'rxjs';
import * as _ from 'lodash';
import {AssociationHelper} from 'app/storage/association.storage';
import {Router} from '@angular/router';
import {schemaColumns} from 'app/form/meeting-form/schema-columns';

const SERVICE_TYPES: ApplicationType[] = ['service1', 'service2', 'service3'];

// PSS surveys CSV: [column prefix, association field, JSON schema]; columns follow the schemas
const PSS_SURVEY_SOURCES: [string, string, string][] = [
    ['Project meeting 1', 'project_survey_form_1', 'survey_project_meeting'],
    ['Project meeting 2', 'project_survey_form_2', 'survey_project_meeting'],
    ['Project advice', 'project_survey_advice', 'survey_project_advice'],
    ['EPE final', 'epe_survey', 'survey_epe'],
    ['Project final', 'project_survey', 'survey_project_pss'],
]
const PSS_SURVEY_COLUMNS: {column: string, field: string, key: string}[] = []
for (const [prefix, field, schema] of PSS_SURVEY_SOURCES)
    for (const key of schemaColumns(schema))
        PSS_SURVEY_COLUMNS.push({column: `${prefix}: ${key}`, field, key})


@Component({
    selector: 'surveys',
    templateUrl: './surveys.component.html',
    styleUrls: ['../sop.component.scss', '../services/services.component.scss']
})
export class SurveysComponent implements OnInit {
    loading: boolean = true
    sortSelected = 'Project'
    sortDirection = 'ASC'
    headerRow = [
        'ID',
        'Project',
        'EPE',
        'Status',
        'Project Survey 1',
        'Project Survey 2',
        'Project Survey 3',
        'Project Survey 4',
        'Final Epe Survey',
        'Final Project Survey'
    ]

    surveysHeaderRow = [
        'ID',
        'Project',
        'EPE',
        "Project meeting 1: How helpful was the call with the expert? 1 (poor) – 5 (excellent)",
        "Project meeting 1: Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)",
        "Project meeting 1: Free comment box",

        "Project meeting 2: How helpful was the call with the expert? 1 (poor) – 5 (excellent)",
        "Project meeting 2: Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)",
        "Project meeting 2: Free comment box",

        "Project meeting 3: How helpful was the call with the expert? 1 (poor) – 5 (excellent)",
        "Project meeting 3: Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)",
        "Project meeting 3: Free comment box",

        "Project meeting 4: How helpful was the call with the expert? 1 (poor) – 5 (excellent)",
        "Project meeting 4: Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)",
        "Project meeting 4: Free comment box",

        "EPE final: How would you rank the experience with the platform? 1 (poor) – 5 (excellent)",
        "EPE final: How would you rank the interaction and communication with the project representative? 1 (poor) – 5 (excellent)",
        "EPE final: Please add any further comments or suggestions",
        'EPE final: Please add a quote on your service delivery activities that we may use and publish in a report of "testimonials". Approx. 4–5 lines. Focus on describing your support in addressing the project\'s needs.',

        "Project final: Was the expert knowledgeable in the subject matter? 1 (strongly disagree) – 5 (strongly agree)",
        "Project final: How was your communication with the expert? 1 (difficult to understand) – 5 (easy to understand)",
        "Project final: Did the expert provide useful knowledge or recommendations? 1 (strongly disagree) – 5 (strongly agree)",
        "Project final: How would you rank the overall experience with the expert? 1 (poor) – 5 (excellent)",
        "Project final: How relevant were the expert's suggestions to your project's needs? 1 (not relevant) – 5 (highly relevant)",
        "Project final: How likely are you to implement the expert's recommendations? 1 (not likely) – 5 (highly likely)",
        "Project final: What are your planned next steps for the project's future standardisation strategy?",
        "Project final: Has your project's understanding of standards and standardisation improved thanks to the HSbooster service? 1 (not at all) – 5 (very much so)",
        "Project final: Please tell us more…",
        "Project final: Would you consider involving standards consultations like HSbooster in future proposals at:",
        "Project final: Were your queries or issues resolved effectively in a timely manner? 1 (strongly disagree) – 5 (strongly agree)",
        "Project final: How would you rank the overall HSbooster support? 1 (poor) – 5 (excellent)",
        "Project final: How likely are you to recommend HSbooster to a colleague? 1 (not likely) – 5 (very likely)",
        "Project final: What was the most valuable aspect of your experience with HSbooster?",
        "Project final: What was the most challenging aspect of your experience with HSbooster?",
        "Project final: Please add any further comments or suggestions",
        "Project final: Have you made use of the HSbooster training academy material?",
        "Project final: Tell us which modules you have accessed",
        "Project final: Will you apply for follow-up support from HSbooster in the future?",
        'Project final: Please add a quote about your experience with HSbooster.eu that we may use to publish in a report of "testimonials" and/or web and socials. Approx. 4–5 lines. Focus on your needs and what you achieved after the consultancy with HSbooster.',
]


    pssSurveysHeaderRow = [
        'ID',
        'Project',
        'EPE',
        ...PSS_SURVEY_COLUMNS.map(c => c.column)
    ]

    epes: any[] | null = []
    associations: any[] | null = []
    projects: any[] | null = []
    surveys: any[] | null = []
    pssSurveys: any[] | null = []


    showAlert: boolean = false
    alertMessage: string = ''
    alertType: Alert = Alert.SUCCESS


    constructor(
        private applicationsService: ApplicationsService,
        private associationService: AssociationsService,
        private cache: SopStorage,
        private auth: SessionStorage,
        private helper: AssociationHelper,
        private router: Router
    ) {
    }

    ngOnInit(): void {

        const cachedProjects = this.cache.getProjects()
        const cachedEpes = this.cache.getEpes()

        forkJoin({
            projects: !cachedProjects ? this.applicationsService.getAll({ include_user: true }) : of(null),
            epes: !cachedEpes ? this.applicationsService.getAll({ type: 'epe', include_user: true }) : of(null),
            associations: this.associationService.getAllServices()
        })
            .pipe(
                catchError(
                    err => {
                        this.addAlert("Association retriving error", Alert.ERROR)
                        return EMPTY
                    }
                )
            )
            .subscribe(
                then => {
                    this.projects = then.projects
                        ? then.projects
                            .filter((a: any) => SERVICE_TYPES.includes(a.application_type))
                            .map((app: any) => ({
                                ...(app.form_data || {}),
                                ...(app.user_info || {}),
                                ...app,
                                id: app.user_id ?? app.id,
                                application_id: app._id?.['$oid'] ?? app._id,
                                sop_status: app.status,
                            }))
                        : cachedProjects
                    this.epes = then.epes
                        ? then.epes.map((app: any) => ({
                            ...(app.form_data || {}),
                            ...(app.user_info || {}),
                            ...app,
                            id: app.user_id ?? app.id,
                            application_id: app._id?.['$oid'] ?? app._id,
                            sop_status: app.status,
                        }))
                        : cachedEpes
                    this.associations = then.associations?.map(
                        association => {
                            const pid = association.project_application_id ?? association.project_id
                            const eid = association.epe_application_id ?? association.epe_id
                            const project = this.projects?.find(p => p.application_id == pid) ?? this.projects?.find(p => p.id == pid)
                            const epe = this.epes?.find(e => e.application_id == eid) ?? this.epes?.find(e => e.id == eid)
                            return {
                                ...association,
                                project_id: pid,
                                epe_id: eid,
                                'ID': pid + "_" + eid,
                                'Project': project?.project_acronym,
                                'EPE': (epe?.name ?? '') + " " + (epe?.surname ?? ''),
                                'Status': association.association_status,
                                'Project Survey 1': association.project_survey_form_1 ? "DONE" : "",
                                'Project Survey 2': association.project_survey_form_2 ? "DONE" : "",
                                'Project Survey 3': association.project_survey_form_3 ? "DONE" : "",
                                'Project Survey 4': association.project_survey_form_4 ? "DONE" : "",
                                'Final Epe Survey': association.epe_survey ? "DONE" : "",
                                'Final Project Survey': association.project_survey ? "DONE" : "",
                            }
                        }
                    )


                    // Mentoring surveys export (service1, plus legacy associations); PSS has its own export
                    this.surveys = then.associations?.filter(association => !this.isPss(association)).map(
                        association => {
                            const pid = association.project_application_id ?? association.project_id
                            const eid = association.epe_application_id ?? association.epe_id
                            const project = this.projects?.find(p => p.application_id == pid) ?? this.projects?.find(p => p.id == pid)
                            const epe = this.epes?.find(e => e.application_id == eid) ?? this.epes?.find(e => e.id == eid)
                            return {
                                'ID': pid + "_" + eid,
                                'Project': project?.project_acronym,
                                'EPE': (epe?.name ?? '') + " " + (epe?.surname ?? ''),

                                "Project meeting 1: How helpful was the call with the expert? 1 (poor) – 5 (excellent)": association.project_survey_form_1 && association.project_survey_form_1['How helpful was the call with the expert? 1 (poor) – 5 (excellent)'] ? association.project_survey_form_1['How helpful was the call with the expert? 1 (poor) – 5 (excellent)'] : "",
                                "Project meeting 1: Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)": association.project_survey_form_1 && association.project_survey_form_1['Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)'] ? association.project_survey_form_1['Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)'] : "",
                                "Project meeting 1: Free comment box": association.project_survey_form_1 && association.project_survey_form_1['Free comment box'] ? association.project_survey_form_1['Free comment box'] : "",

                                "Project meeting 2: How helpful was the call with the expert? 1 (poor) – 5 (excellent)": association.project_survey_form_2 && association.project_survey_form_2['How helpful was the call with the expert? 1 (poor) – 5 (excellent)'] ? association.project_survey_form_2['How helpful was the call with the expert? 1 (poor) – 5 (excellent)'] : "",
                                "Project meeting 2: Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)": association.project_survey_form_2 && association.project_survey_form_2['Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)'] ? association.project_survey_form_2['Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)'] : "",
                                "Project meeting 2: Free comment box": association.project_survey_form_2 && association.project_survey_form_2['Free comment box'] ? association.project_survey_form_2['Free comment box'] : "",

                                "Project meeting 3: How helpful was the call with the expert? 1 (poor) – 5 (excellent)": association.project_survey_form_3 && association.project_survey_form_3['How helpful was the call with the expert? 1 (poor) – 5 (excellent)'] ? association.project_survey_form_3['How helpful was the call with the expert? 1 (poor) – 5 (excellent)'] : "",
                                "Project meeting 3: Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)": association.project_survey_form_3 && association.project_survey_form_3['Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)'] ? association.project_survey_form_3['Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)'] : "",
                                "Project meeting 3: Free comment box": association.project_survey_form_3 && association.project_survey_form_3['Free comment box'] ? association.project_survey_form_3['Free comment box'] : "",

                                "Project meeting 4: How helpful was the call with the expert? 1 (poor) – 5 (excellent)": association.project_survey_form_4 && association.project_survey_form_4['How helpful was the call with the expert? 1 (poor) – 5 (excellent)'] ? association.project_survey_form_4['How helpful was the call with the expert? 1 (poor) – 5 (excellent)'] : "",
                                "Project meeting 4: Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)": association.project_survey_form_4 && association.project_survey_form_4['Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)'] ? association.project_survey_form_4['Do you have a better understanding of how to approach your standardisation activities? 1 (poor) – 5 (excellent)'] : "",
                                "Project meeting 4: Free comment box": association.project_survey_form_4 && association.project_survey_form_4['Free comment box'] ? association.project_survey_form_4['Free comment box'] : "",

                                "EPE final: How would you rank the experience with the platform? 1 (poor) – 5 (excellent)": association.epe_survey && association.epe_survey['How would you rank the experience with the platform? 1 (poor) – 5 (excellent)'] ? association.epe_survey['How would you rank the experience with the platform? 1 (poor) – 5 (excellent)'] : "",
                                "EPE final: How would you rank the interaction and communication with the project representative? 1 (poor) – 5 (excellent)": association.epe_survey && association.epe_survey['How would you rank the interaction and communication with the project representative? 1 (poor) – 5 (excellent)'] ? association.epe_survey['How would you rank the interaction and communication with the project representative? 1 (poor) – 5 (excellent)'] : "",
                                "EPE final: Please add any further comments or suggestions": association.epe_survey && association.epe_survey['Please add any further comments or suggestions'] ? association.epe_survey['Please add any further comments or suggestions'] : "",
                                'EPE final: Please add a quote on your service delivery activities that we may use and publish in a report of "testimonials". Approx. 4–5 lines. Focus on describing your support in addressing the project\'s needs.': association.epe_survey && association.epe_survey['Please add a quote on your service delivery activities that we may use and publish in a report of "testimonials". Approx. 4–5 lines. Focus on describing your support in addressing the project\'s needs.'] ? association.epe_survey['Please add a quote on your service delivery activities that we may use and publish in a report of "testimonials". Approx. 4–5 lines. Focus on describing your support in addressing the project\'s needs.'] : "",

                                "Project final: Was the expert knowledgeable in the subject matter? 1 (strongly disagree) – 5 (strongly agree)": association.project_survey && association.project_survey['Was the expert knowledgeable in the subject matter? 1 (strongly disagree) – 5 (strongly agree)'] ? association.project_survey['Was the expert knowledgeable in the subject matter? 1 (strongly disagree) – 5 (strongly agree)'] : "",
                                "Project final: How was your communication with the expert? 1 (difficult to understand) – 5 (easy to understand)": association.project_survey && association.project_survey['How was your communication with the expert? 1 (difficult to understand) – 5 (easy to understand)'] ? association.project_survey['How was your communication with the expert? 1 (difficult to understand) – 5 (easy to understand)'] : "",
                                "Project final: Did the expert provide useful knowledge or recommendations? 1 (strongly disagree) – 5 (strongly agree)": association.project_survey && association.project_survey['Did the expert provide useful knowledge or recommendations? 1 (strongly disagree) – 5 (strongly agree)'] ? association.project_survey['Did the expert provide useful knowledge or recommendations? 1 (strongly disagree) – 5 (strongly agree)'] : "",
                                "Project final: How would you rank the overall experience with the expert? 1 (poor) – 5 (excellent)": association.project_survey && association.project_survey['How would you rank the overall experience with the expert? 1 (poor) – 5 (excellent)'] ? association.project_survey['How would you rank the overall experience with the expert? 1 (poor) – 5 (excellent)'] : "",
                                "Project final: How relevant were the expert's suggestions to your project's needs? 1 (not relevant) – 5 (highly relevant)": association.project_survey && association.project_survey['How relevant were the expert\'s suggestions to your project\'s needs? 1 (not relevant) – 5 (highly relevant)'] ? association.project_survey['How relevant were the expert\'s suggestions to your project\'s needs? 1 (not relevant) – 5 (highly relevant)'] : "",
                                "Project final: How likely are you to implement the expert's recommendations? 1 (not likely) – 5 (highly likely)": association.project_survey && association.project_survey['How likely are you to implement the expert\'s recommendations? 1 (not likely) – 5 (highly likely)'] ? association.project_survey['How likely are you to implement the expert\'s recommendations? 1 (not likely) – 5 (highly likely)'] : "",
                                "Project final: What are your planned next steps for the project's future standardisation strategy?": association.project_survey && association.project_survey['What are your planned next steps for the project\'s future standardisation strategy?'] ? association.project_survey['What are your planned next steps for the project\'s future standardisation strategy?'] : "",
                                "Project final: Has your project's understanding of standards and standardisation improved thanks to the HSbooster service? 1 (not at all) – 5 (very much so)": association.project_survey && association.project_survey['Has your project\'s understanding of standards and standardisation improved thanks to the HSbooster service? 1 (not at all) – 5 (very much so)'] ? association.project_survey['Has your project\'s understanding of standards and standardisation improved thanks to the HSbooster service? 1 (not at all) – 5 (very much so)'] : "",
                                "Project final: Please tell us more…": association.project_survey && association.project_survey['Please tell us more…'] ? association.project_survey['Please tell us more…'] : "",
                                "Project final: Would you consider involving standards consultations like HSbooster in future proposals at:": association.project_survey && association.project_survey['Would you consider involving standards consultations like HSbooster in future proposals at:'] ? association.project_survey['Would you consider involving standards consultations like HSbooster in future proposals at:'] : "",
                                "Project final: Were your queries or issues resolved effectively in a timely manner? 1 (strongly disagree) – 5 (strongly agree)": association.project_survey && association.project_survey['Were your queries or issues resolved effectively in a timely manner? 1 (strongly disagree) – 5 (strongly agree)'] ? association.project_survey['Were your queries or issues resolved effectively in a timely manner? 1 (strongly disagree) – 5 (strongly agree)'] : "",
                                "Project final: How would you rank the overall HSbooster support? 1 (poor) – 5 (excellent)": association.project_survey && association.project_survey['How would you rank the overall HSbooster support? 1 (poor) – 5 (excellent)'] ? association.project_survey['How would you rank the overall HSbooster support? 1 (poor) – 5 (excellent)'] : "",
                                "Project final: How likely are you to recommend HSbooster to a colleague? 1 (not likely) – 5 (very likely)": association.project_survey && association.project_survey['How likely are you to recommend HSbooster to a colleague? 1 (not likely) – 5 (very likely)'] ? association.project_survey['How likely are you to recommend HSbooster to a colleague? 1 (not likely) – 5 (very likely)'] : "",
                                "Project final: What was the most valuable aspect of your experience with HSbooster?": association.project_survey && association.project_survey['What was the most valuable aspect of your experience with HSbooster?'] ? association.project_survey['What was the most valuable aspect of your experience with HSbooster?'] : "",
                                "Project final: What was the most challenging aspect of your experience with HSbooster?": association.project_survey && association.project_survey['What was the most challenging aspect of your experience with HSbooster?'] ? association.project_survey['What was the most challenging aspect of your experience with HSbooster?'] : "",
                                "Project final: Please add any further comments or suggestions": association.project_survey && association.project_survey['Please add any further comments or suggestions'] ? association.project_survey['Please add any further comments or suggestions'] : "",
                                "Project final: Have you made use of the HSbooster training academy material?": association.project_survey && association.project_survey['Have you made use of the HSbooster training academy material?'] ? association.project_survey['Have you made use of the HSbooster training academy material?'] : "",
                                "Project final: Tell us which modules you have accessed": association.project_survey && association.project_survey['Tell us which modules you have accessed'] ? association.project_survey['Tell us which modules you have accessed'] : "",
                                "Project final: Will you apply for follow-up support from HSbooster in the future?": association.project_survey && association.project_survey['Will you apply for follow-up support from HSbooster in the future?'] ? association.project_survey['Will you apply for follow-up support from HSbooster in the future?'] : "",
                                'Project final: Please add a quote about your experience with HSbooster.eu that we may use to publish in a report of "testimonials" and/or web and socials. Approx. 4–5 lines. Focus on your needs and what you achieved after the consultancy with HSbooster.': association.project_survey && association.project_survey['Please add a quote about your experience with HSbooster.eu that we may use to publish in a report of "testimonials" and/or web and socials. Approx. 4–5 lines. Focus on your needs and what you achieved after the consultancy with HSbooster.'] ? association.project_survey['Please add a quote about your experience with HSbooster.eu that we may use to publish in a report of "testimonials" and/or web and socials. Approx. 4–5 lines. Focus on your needs and what you achieved after the consultancy with HSbooster.'] : "",
                               }
                        }
                    )

                    this.pssSurveys = then.associations?.filter(association => this.isPss(association)).map(
                        association => {
                            const pid = association.project_application_id ?? association.project_id
                            const eid = association.epe_application_id ?? association.epe_id
                            const project = this.findProject(association)
                            const epe = this.epes?.find(e => e.application_id == eid) ?? this.epes?.find(e => e.id == eid)
                            // every column must be present, in header order, for the CSV rows to line up
                            const row: any = {
                                'ID': pid + "_" + eid,
                                'Project': project?.project_acronym,
                                'EPE': (epe?.name ?? '') + " " + (epe?.surname ?? ''),
                            }
                            for (const {column, field, key} of PSS_SURVEY_COLUMNS)
                                row[column] = association[field]?.[key] ?? ""
                            return row
                        }
                    )

                    this.cache.setEpes(this.epes!)
                    this.cache.setProjects(this.projects!)

                    this.associations?.sort(this.statusSort(this.sortDirection, this.sortSelected))
                    this.loading = false
                }
            )
    }

    statusSort(sortDirection: string, sortSelected: string) {
        return function (a: AssociationMongo, b: AssociationMongo) {
            if (sortDirection == 'ASC') {
                return a[sortSelected].localeCompare(b[sortSelected])
            } else {
                return a[sortSelected].localeCompare(b[sortSelected]) * -1
            }
        }
    }

    sortCell(cell: string) {
        if (this.sortDirection == 'ASC') {
            this.sortDirection = 'DESC'
        } else {
            this.sortDirection = 'ASC'
        }
        this.sortSelected = cell
        console.log(this.sortDirection)
        console.log(this.sortSelected)
        this.associations?.sort(this.statusSort(this.sortDirection, this.sortSelected))
        console.log(this.associations)
    }

    private findProject(association: any): any {
        const pid = association.project_application_id ?? association.project_id
        return this.projects?.find(p => p.application_id == pid) ?? this.projects?.find(p => p.id == pid)
    }

    private isPss(association: any): boolean {
        return this.findProject(association)?.application_type === 'service2'
    }

    exportAuthorized() {
        return this.auth.isAdmin() || this.auth.isCallManager() || this.auth.isProjectBoard()
    }

    stripHtmlTags(content: string): string {
        const div = document.createElement('div');
        div.innerHTML = content;
        return div.textContent || div.innerText || '';
    }

    exportCSV() {
        var surveys = [...this.surveys!]
        var surveys_picked = _.map(surveys, ass => _.pick(ass, this.surveysHeaderRow))
        surveys_picked.forEach(survey => {
            for (const key in survey) {
                if (survey.hasOwnProperty(key)) {
                    survey[key] = this.stripHtmlTags(survey[key]);
                }
            }
        });

        let csvOptions = {
            fieldSeparator: ';',
            quoteStrings: '"',
            decimalseparator: '.',
            showLabels: true,
            showTitle: true,
            title: "SOP Services",
            useBom: true,
            noDownload: false,
            headers: this.surveysHeaderRow
        };

        new AngularCsv(surveys_picked, 'SOP-Surveys-HSBooster', csvOptions);
    }

    exportPssCSV() {
        const surveys_picked = this.pssSurveys!.map(survey => {
            const picked: any = _.pick(survey, this.pssSurveysHeaderRow)
            for (const key of Object.keys(picked))
                picked[key] = this.stripHtmlTags(picked[key])
            return picked
        })

        const csvOptions = {
            fieldSeparator: ';',
            quoteStrings: '"',
            decimalseparator: '.',
            showLabels: true,
            showTitle: true,
            title: "SOP PSS Surveys",
            useBom: true,
            noDownload: false,
            headers: this.pssSurveysHeaderRow
        };

        new AngularCsv(surveys_picked, 'SOP-PSS-Surveys-HSBooster', csvOptions);
    }

    openNewTab(url: any) {
        window.open(url)
    }

    getMeetingComplete(meetingForm: any) {
        if (!meetingForm) {
            return ''
        }
        if (meetingForm['complete']) {
            return 'DONE'
        }

        return ''
    }

    getMeetingStatus(meetingForm: any) {
        if (!meetingForm) {
            return ''
        }
        if (meetingForm['dateSetted']) {
            if (meetingForm['meetingCompleted']) {
                return 'DONE'
            } else {
                return 'SET'
            }
        }
        return ''
    }

    getMeetingDate(meetingForm: any) {
        if (!meetingForm) {
            return ''
        }
        if (meetingForm['dateSetted'] && meetingForm['date']) {
            return meetingForm['date']
        }
        return ''
    }

    addAlert(message: string, type: Alert) {
        this.alertMessage = message
        this.alertType = type
        this.showAlert = true
    }

    isCurrentEpe(status: string | undefined): boolean {

        switch (status) {
            case 'open':
            case 'waiting':
            case 'not_approved':
            case undefined:
                return false
        }
        return true

    }

    goToService(project: string | undefined, epe: string | undefined) {

        if (!project || !epe)
            return

        let projectAssociations = this.associations?.filter(x => x.project_id == project)

        let epe_id = projectAssociations!.find(x => this.isCurrentEpe(x.association_status))?.epe_id

        if (epe_id)
            this.helper.setSelectedEpe(epe_id)
        else
            this.helper.setSelectedEpe('')

        let meeting = projectAssociations!.find(x => x.epe_id == epe_id && x.project_id == project)?.meeting

        if (meeting)
            this.helper.setMeetingForm(meeting)

        this.helper.setSelectedProject(project)

        let form = this.helper.getMeetingForm()
        if (this.auth.isProject() && form <= 1) {
            this.addAlert("Please wait until the Epe complete at least one form", Alert.WARNING)
            return
        }
        if (form === 0 || !form)
            form = 1
        form = this.auth.isProject() && form < 7 ? form - 1 : form
        console.log(form)
        let route = this.auth.isProject() ? 'summary' : 'first'
        if (form < 6) {
            console.log("There")
            this.router.navigateByUrl(`form/(meeting-form:meeting_${form}/(meeting_${form}:${route}))`)
        } else if (this.auth.isProjectBoard() || this.auth.isCallManager() || this.auth.isAdmin()) {
            this.router.navigateByUrl(`form/(meeting-form:survey-form/(survey-form:admin))`)
        } else if (form == 6) {
            this.router.navigateByUrl(`form/(meeting-form:survey-form)`)
        } else {
            console.log("here")
            this.router.navigateByUrl(`form/(meeting-form:survey-form/(survey-form:summary))`)
        }

    }

    goToDashboard(project: string | undefined, epe: string | undefined) {
        if (!project || !epe)
            return

        const projectAssociations = this.associations?.filter(x => x.project_id == project) || []
        const epe_id = projectAssociations.find(x => this.isCurrentEpe(x.association_status))?.epe_id
            ?? projectAssociations[0]?.epe_id
            ?? epe

        this.helper.setSelectedEpe(epe_id)
        this.helper.setSelectedProject(project)
        this.router.navigateByUrl('survey-dashboard')
    }

}
